import React, { useState, useEffect, useCallback } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { db } from "../firebase";
import { Heading } from "./heading";
import { showAlert } from "../alertService";
import { fetchPublicVenues, venuesForCombo } from "../utils/landingsInventory";

const ACCENT = "#1b4d2e";

// Matriz de landings combo (evento × ubicación), p. ej. /bodas/queretaro.
// Cada celda muestra cuántos venues públicos tendría esa página y permite
// apagarla a mano. Reglas de visibilidad de un combo en el homesite:
//   1. tipo Y ubicación activos,
//   2. al menos 1 venue tageado en la zona (0 venues → 404 automático),
//   3. no estar apagado a mano aquí (eventTypes/{id}.combosDesactivados).
export default function LandingCombosMatrix() {
  const [eventTypes, setEventTypes] = useState(null);
  const [ciudades, setCiudades] = useState(null);
  const [venues, setVenues] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      const [etSnap, cSnap, publicVenues] = await Promise.all([
        getDocs(collection(db, "eventTypes")),
        getDocs(collection(db, "ciudades")),
        fetchPublicVenues(),
      ]);
      const byOrden = (a, b) => (a.orden || 0) - (b.orden || 0);
      setEventTypes(
        etSnap.docs.map((d) => ({ id: d.id, ...d.data() })).sort(byOrden)
      );
      setCiudades(
        cSnap.docs.map((d) => ({ id: d.id, ...d.data() })).sort(byOrden)
      );
      setVenues(publicVenues);
    } catch (e) {
      console.error("Error cargando la matriz de combos:", e);
      setError(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleCombo = async (et, ciudad, isOff) => {
    try {
      await updateDoc(doc(db, "eventTypes", et.id), {
        combosDesactivados: isOff
          ? arrayRemove(ciudad.id)
          : arrayUnion(ciudad.id),
      });
      setEventTypes((arr) =>
        arr.map((e) => {
          if (e.id !== et.id) return e;
          const prev = e.combosDesactivados || [];
          return {
            ...e,
            combosDesactivados: isOff
              ? prev.filter((id) => id !== ciudad.id)
              : [...prev, ciudad.id],
          };
        })
      );
    } catch (e) {
      showAlert("Error al actualizar el combo: " + e.message, "error");
    }
  };

  if (error) return null;

  const loading = !eventTypes || !ciudades || !venues;
  const activeTipos = (eventTypes || []).filter((e) => e.activo);
  const activeCiudades = (ciudades || []).filter((c) => c.activo);

  return (
    <div className="mt-10">
      <Heading level={3}>Landings combo (evento × ubicación)</Heading>
      <p className="text-sm text-gray-500 mt-1 mb-4">
        Páginas como /bodas/cdmx. Cada celda muestra cuántos venues públicos
        tendría esa página. Con 0 venues la página se apaga sola (404); con
        venues puedes apagarla a mano haciendo clic en la celda. Solo se
        listan tipos y ubicaciones activos.
      </p>

      {loading ? (
        <div className="h-32 animate-pulse rounded-xl bg-zinc-100" />
      ) : activeTipos.length === 0 || activeCiudades.length === 0 ? (
        <p className="text-sm text-zinc-500">
          Activa al menos un tipo de evento y una ubicación para ver la matriz.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-zinc-200 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50">
                <th className="px-4 py-3 text-left font-medium text-zinc-500">
                  Tipo de evento
                </th>
                {activeCiudades.map((c) => (
                  <th
                    key={c.id}
                    className="px-3 py-3 text-center font-medium text-zinc-500 whitespace-nowrap"
                  >
                    {c.titulo?.ES || c.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {activeTipos.map((et) => (
                <tr key={et.id} className="border-b border-zinc-100 last:border-0">
                  <td className="px-4 py-2.5 font-medium text-zinc-800 whitespace-nowrap">
                    {et.label?.ES || et.id}
                  </td>
                  {activeCiudades.map((c) => {
                    const count = venuesForCombo(venues, et.id, c.name).length;
                    const isOff = (et.combosDesactivados || []).includes(c.id);
                    const url = `/${et.slug?.ES || et.id}/${c.slug || c.id}`;
                    if (count === 0) {
                      return (
                        <td key={c.id} className="px-3 py-2.5 text-center">
                          <span
                            className="text-zinc-300"
                            title={`${url} — sin venues: la página no se publica`}
                          >
                            0
                          </span>
                        </td>
                      );
                    }
                    return (
                      <td key={c.id} className="px-3 py-2.5 text-center">
                        <button
                          type="button"
                          onClick={() => toggleCombo(et, c, isOff)}
                          title={
                            isOff
                              ? `${url} — apagada a mano (${count} venue${count === 1 ? "" : "s"}). Clic para publicar.`
                              : `${url} — publicada con ${count} venue${count === 1 ? "" : "s"}. Clic para apagar.`
                          }
                          className={`inline-flex min-w-[2.5rem] items-center justify-center rounded-full px-2.5 py-1 text-xs font-semibold transition ${
                            isOff
                              ? "bg-zinc-100 text-zinc-400 line-through hover:bg-zinc-200"
                              : "text-white hover:opacity-90"
                          }`}
                          style={isOff ? undefined : { backgroundColor: ACCENT }}
                        >
                          {count}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && (
        <p className="text-xs text-zinc-400 mt-2">
          Verde: publicada (el número es cuántos venues lista). Tachado:
          apagada a mano. 0: sin venues públicos — se apaga sola.
        </p>
      )}
    </div>
  );
}
